import { useEffect, useRef } from "react";

// Web implementation. A sibling `.native.ts` provides a no-op stub so the
// orchestrator stays platform-agnostic — Metro picks `.native.ts` on native
// builds; Vite/webpack pick this file on web.
export function useBrowserHistorySync({
  stateTag,
  canGoBack,
  goBack,
}: {
  stateTag: string;
  canGoBack: boolean;
  goBack: () => void;
}) {
  const tagRef = useRef(stateTag);
  const navigatingBack = useRef(false);

  useEffect(() => {
    if (stateTag !== tagRef.current) {
      if (!navigatingBack.current && canGoBack) {
        // Push an entry so there is something for the browser's back button
        // (and gestures/keyboard shortcuts) to pop — popstate only fires when
        // the stack is non-empty. Storing `{ screen }` instead of `null` makes
        // the entry self-describing in DevTools and surfaces it as
        // `event.state` on popstate, which is useful for debugging and
        // future consistency checks.

        // TODO GRO-633: the UX is not great when the flow has multiple points
        // where back is not allowed. See
        // https://linear.app/medbillai/issue/GRO-633
        window.history.pushState({ screen: stateTag }, "");
      }
      tagRef.current = stateTag;
      navigatingBack.current = false;
    }
  }, [stateTag, canGoBack]);

  useEffect(() => {
    const handlePopState = () => {
      if (canGoBack) {
        // popstate is our single choke point for navigation-driven goBack —
        // flip the flag so the forward-push effect skips its push when the
        // resulting state change lands.
        navigatingBack.current = true;
        goBack();
        return;
      }
      // No back handler on this screen. Warn the user that pressing back
      // will leave the app and let them confirm or cancel. On cancel,
      // re-push so the stack stays non-empty and popstate keeps firing.
      const leave = window.confirm("Going back will leave the app. Continue?");
      if (!leave) {
        window.history.pushState({ screen: stateTag }, "");
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [stateTag, canGoBack, goBack]);

  // Routing the in-app back button through `history.back()` keeps popstate
  // the only place that calls `goBack()` for back navigation, so the
  // push/pop bookkeeping can't get out of sync. The orchestrator never
  // touches `window.history` directly.
  return {
    handleBack: () => window.history.back(),
  };
}
